import React, { useState, useCallback, useEffect } from 'react';
import { GetUTCTime } from "../../Helpers/GetUTCTime";
import { useDispatch, useSelector } from "react-redux";
import { apiCallintroductoryTextMedia } from "../../Helpers/ApiCall";
import ImageCarousel from "../LineDetails/ImageCarousel";
import DeleteMediaModal from "../../Pages/ReportLibrary/DeleteMediaModal";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNavigate } from "react-router-dom";
import { CATEGORY_SCROLL_CONTAINER_ID } from "../../Config/App-constants";
import { getMediaDomId } from "../../Helpers/MediaDomId";
import { debounce } from 'lodash';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    DragOverlay,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy,
} from '@dnd-kit/sortable';

import Grid from './Grid';
import SortableItem from './SortableItem';
import Item from './Item';

const IntroTextMedia = ({ introTextMedia, setShowRMLpopup, inspectionResponse, templateId, categoryId, categoryUuid, categoryData }) => {
    const [items, setItems] = useState(introTextMedia || []);
    const [activeId, setActiveId] = useState(null);
    const [inputChanged, setInputChanged] = useState(false);
    const [videoPlayer, setVideoPlayer] = useState(false);
    const [videolink, setVideoLink] = useState("");
    const [videoId, SetVideoId] = useState("");
    const [openDeleteMediaModal, setOpenDeleteMediaModal] = useState(false);
    const [deleteMedia, setDeleteMedia] = useState("");
    const navigate = useNavigate();
    const storeData = useSelector((state) => state.CommonReducer);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
    const dispatch = useDispatch();
    const [captions, setCaptions] = useState({});

    useEffect(() => {
        setItems(introTextMedia || []);
    }, [introTextMedia]);
    const activeItem = items.find((item) => item.id === activeId);

    const handleDragStart = useCallback((event) => {
        setActiveId(event.active.id);
    }, []);

    const saveOrderToServer = async (items) => {
        const updateTime = await GetUTCTime();
        const reorderedItems = items.map((item, index) => ({
            ...item,
            order: index,
            updated_at: updateTime
        }));

        const formData = {
            // your API payload
            template_id: templateId,
            category_id: categoryId,
            inspection_id: storeData?.inspectionData?.data?.inspection_id,
            guid: storeData?.inspectionData?.data?.guid,
            introductory_text_media: reorderedItems,
        };

        try {
            await apiCallintroductoryTextMedia(dispatch, formData);
        } catch (error) {
            console.error("Error saving drag order:", error);
        }
    };

    // 👇 Debounced function, only triggers after 500ms of inactivity
    const debouncedSaveOrder = debounce(saveOrderToServer, 500);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                const updatedItems = arrayMove(items, oldIndex, newIndex);

                // 🔁 Sync captions as well
                // setCaptions((prevCaptions) => arrayMove(prevCaptions, oldIndex, newIndex));

                // 🔢 Add/update image_index
                const indexedItems = updatedItems.map((item, index) => ({
                    ...item,
                    order: index,
                }));

                debouncedSaveOrder(indexedItems);

                return indexedItems;
            });
        }

        setActiveId(null);
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
    }, []);


    const pinturaNavigate = (imageUrl, image) => {
        // Same restore hand-off as ShowMoreLess.js's pinturaNavigate (PALM-15231): the tapped image and the
        // category page's scroll position, handed back when Save/Close returns here. Replaced, not pushed, so
        // the editor never stays in browser history (no Forward button back into it).
        const mediaId = image?.id ?? image?.onlineUrl;
        const scrollY = document.getElementById(CATEGORY_SCROLL_CONTAINER_ID)?.scrollTop ?? 0;
        navigate(`/pinturaeditor`, {
            replace: true,
            state: {
                imageUrl,
                image,
                inspectionResponse,
                isPdf: "0",
                templateId,
                categoryId,
                categoryUuid,
                action: "introTextMedia",
                isNarrative: "0",
                introTextMedia,
                mediaId,
                scrollY,
            },
        });
    };

    const handleClose = () => {
        setOpenDeleteMediaModal(false);
    };

    useEffect(() => {
        if (introTextMedia && Array.isArray(introTextMedia)) {
            const captionMap = {};
            introTextMedia.forEach((media) => {
                captionMap[media.uuid] = media.description || "";
            });
            setCaptions(captionMap);
        }
    }, [introTextMedia]);

    const HandleSaveCaption = async (id, caption) => {
        const updateTime = await GetUTCTime();
        // Filter only the media with updated descriptions
        const SelectIntroductoryTextMedia = introTextMedia.find((media) => media.id === id);
        if (SelectIntroductoryTextMedia) {
            SelectIntroductoryTextMedia.description = caption;
            SelectIntroductoryTextMedia.updated_at = updateTime;
        } else {
            setInputChanged(false);
            return; // Skip the API call if no changes
        }
        // ✅ Update local `items` state so UI stays in sync
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === id
                    ? { ...item, description: caption, updated_at: updateTime }
                    : item
            )
        );

        const formData = {
            template_id: templateId,
            category_id: categoryId,
            inspection_id: storeData?.inspectionData?.data?.inspection_id,
            guid: storeData?.inspectionData?.data?.guid,
            introductory_text_media: [SelectIntroductoryTextMedia], // Include only updated media
        };
        try {
            await apiCallintroductoryTextMedia(dispatch, formData);
        } catch (error) {
            console.error("Error during API call or data update:", error);
        } finally {
            setInputChanged(false); // Reset input change state
        }
    };
    const HandleDeleteIntroMedia = async () => {
        try {
            const updateTime = await GetUTCTime();
            const SelectIntroductoryTextMedia = introTextMedia.find((media) => media.id === deleteMedia?.id);

            if (SelectIntroductoryTextMedia) {
                SelectIntroductoryTextMedia.is_deleted = 1;
                SelectIntroductoryTextMedia.updated_at = updateTime;
            } else {
                setInputChanged(false);
                return;
            }
            setItems((prevItems) =>
                prevItems?.map((item) =>
                    item?.id === deleteMedia?.id
                        ? { ...item, is_deleted: 1, updated_at: updateTime }
                        : item
                )
            );
            const formData = {
                template_id: templateId,
                category_id: categoryId,
                inspection_id: storeData?.inspectionData?.data?.inspection_id,
                guid: storeData?.inspectionData?.data?.guid,
                introductory_text_media: [SelectIntroductoryTextMedia],
            };
            await apiCallintroductoryTextMedia(dispatch, formData);
        } catch (error) {
            console.error("Error during API call or data update:", error);
        } finally {
            setInputChanged(false);
            setOpenDeleteMediaModal(false);
        }
    };

    useEffect(() => {
        const captionMap = {};
        introTextMedia?.forEach((media) => {
            captionMap[media.id] = media?.description || "";
        });
        setCaptions(captionMap);
    }, [introTextMedia]);



    return (
        <>
            {videoPlayer && (
                <ImageCarousel
                    inspectionResponse={inspectionResponse}
                    videolink={videolink}
                    mediaData={introTextMedia?.filter((item) => item.filetype.includes("video"))}
                    videoEditor={videoPlayer}
                    setVideoEditor={setVideoPlayer}
                    videoId={videoId}
                />
            )}
            <DeleteMediaModal
                open={openDeleteMediaModal}
                handleClose={handleClose}
                deleteMedia={deleteMedia}
                cancelBtnAction={handleClose}
                submitBtnAction={handleClose}
                handleDeleteRMLMedia={HandleDeleteIntroMedia}
            />
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext items={items?.filter(e => e?.is_deleted !== 1)?.map((item) => item?.id)} strategy={rectSortingStrategy}>
                    <Grid columns={5}>
                        {introTextMedia?.filter(e => e?.is_deleted !== 1)?.length === 0 ? "" : (<div
                            style={{
                                height: '126px',
                                width: '150px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                border: '2px dashed #ccc',
                                cursor: 'pointer',
                            }}
                            onClick={() => setShowRMLpopup(true)}
                        >
                            <AddCircleIcon fontSize='medium' sx={{ color: "#4282e2" }} />
                            <div style={{ textDecoration: 'underline', color: '#4282e2', fontSize: '14px', fontWeight: '600' }}>
                                Click to add media
                            </div>
                        </div>)}

                        {items?.filter(e => e?.is_deleted !== 1 && e?.online == "online")?.sort((a, b) => a.order - b.order)?.map((item, index) => {
                            const bucketUrl = item.is_portal === 1 ? inspectionResponse?.inspectionData?.data?.bucket_url_2 : inspectionResponse?.inspectionData?.data?.bucket_url;
                            const imageThumbnailUrl = `${bucketUrl}${item.thumbnail ? item.thumbnail : item.onlineUrl}`;
                            const imageUrl = `${bucketUrl}${item?.onlineUrl}`;
                            // const videoUrl = `${bucketUrl}${item.onlineUrl}`;
                            // const videoThumbnail = `${bucketUrl}${item.thumbnail ? item.thumbnail : item.onlineUrl}`;
                            return (
                                <SortableItem onPressEdit={() => pinturaNavigate(imageUrl, item)} onPressDelete={() => {
                                    setOpenDeleteMediaModal(true);
                                    setDeleteMedia(item);
                                }} key={item.id} domId={getMediaDomId(categoryUuid, item.id ?? item.onlineUrl)} bucketUrl={bucketUrl} setVideoLink={setVideoLink} setVideoPlayer={setVideoPlayer} SetVideoId={SetVideoId} setCaptions={setCaptions} id={item.id} index={index} thumbnail={item.thumbnail} name={item.name} item={item} imageThumbnailUrl={imageThumbnailUrl} captions={captions} HandleSaveCaption={HandleSaveCaption} />
                            )
                        })}
                    </Grid>
                </SortableContext>

                <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
                    {activeItem ? (
                        <Item id={activeItem.id} thumbnail={activeItem.thumbnail} isDragging />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </>
    );
};

export default IntroTextMedia;
